"use strict";

/* Upstream tool engines, bundled.
   Layer 1 is src/lib/tools.mjs ported verbatim: pure, DOM free, unit tested.
   Layer 2 is src/client/tools.js ported verbatim: binds those engines to the
   DOM. Generated by scripts/bundle_tools.py. Do not edit this file by hand,
   edit the generator so the port stays reproducible.

   Config arrives from #site-tools-config, built by snippets/tools-config.liquid.
   Owner editable values (prices, the founding offer, integration URLs) come
   from theme settings. Methodology (the cost curve, the grade rubric, the
   checklist, timeline offsets) is hardcoded, because an owner edit there would
   silently invalidate the cited research these tools display. */
window.UpstreamTools = (function () {

  /* PORT CHANGE 1 of 7: the proxy address, populated from theme settings once
     the config blob is parsed in the client layer below. */
  var upstreamProxyBase = "";
  const pageSpeedEndpoint = "https://www.googleapis.com/pagespeedonline/v5/runPagespeed";
  const gradeCategories = Object.freeze([
    "performance",
    "accessibility",
    "seo",
    "best-practices"
  ]);
  const gradeScoreKeys = Object.freeze([
    "performance",
    "accessibility",
    "seo",
    "bestPractices"
  ]);

  function invalidUrl(error) {
    return { valid: false, error };
  }

  function normalizeWebsiteUrl(input) {
    const value = String(input ?? "").trim();
    if (!value) return invalidUrl("Enter a website address.");
    if (value.includes("\\") || /\s/.test(value)) {
      return invalidUrl("Enter a complete website address without spaces.");
    }

    const scheme = value.match(/^([a-z][a-z0-9+.-]*):/i)?.[1]?.toLowerCase() ?? "";
    if (scheme && scheme !== "http" && scheme !== "https") {
      return invalidUrl("Use an HTTP or HTTPS website address.");
    }

    let parsed;
    try {
      parsed = new URL(scheme ? value : `https://${value}`);
    } catch {
      return invalidUrl("Enter a valid public website address.");
    }

    if (!["http:", "https:"].includes(parsed.protocol)) {
      return invalidUrl("Use an HTTP or HTTPS website address.");
    }
    if (parsed.username || parsed.password) {
      return invalidUrl("Website addresses with embedded credentials are not accepted.");
    }

    const hostname = parsed.hostname.replace(/\.$/, "").toLowerCase();
    const labels = hostname.split(".");
    const validLabels = labels.length >= 2
      && labels.every((label) => (
        label.length > 0
        && label.length <= 63
        && /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i.test(label)
      ))
      && !/^\d+$/.test(labels.at(-1));
    if (!validLabels) {
      return invalidUrl("Enter a valid public domain, such as example.com.");
    }

    parsed.hostname = hostname;
    return { valid: true, url: parsed.href };
  }

  function buildContextUrl(path, context = {}) {
    const base = "https://upstream-context.invalid";
    const url = new URL(path, base);
    for (const [key, rawValue] of Object.entries(context)) {
      if (rawValue === undefined || rawValue === null || rawValue === "") continue;
      url.searchParams.set(key, String(rawValue));
    }
    if (url.origin === base) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
    return url.href;
  }

  function calculateGrade(scores, rubric) {
    const values = gradeScoreKeys.map((category) => {
      const value = Number(scores?.[category]);
      if (!Number.isFinite(value) || value < 0 || value > 100) {
        throw new TypeError(`Invalid ${category} score.`);
      }
      return value;
    });
    const score = Math.round(values.reduce((total, value) => total + value, 0) / values.length);
    const orderedRubric = [...rubric].sort((left, right) => right.minimum - left.minimum);
    const match = orderedRubric.find((entry) => score >= entry.minimum) ?? orderedRubric.at(-1);
    if (!match) throw new TypeError("A grade rubric is required.");
    return { score, letter: match.letter };
  }

  function buildPageSpeedRequests(normalizedUrl, apiKey) {
    /* PORT CHANGE 2 of 7: prefer the Cloudflare Worker proxy when configured.
       The proxy holds the PageSpeed key as a server side secret, so no key is
       ever present in storefront page source. The original direct-to-Google
       path below is kept intact as a fallback. */
    var proxyBase = String(upstreamProxyBase || "").trim();
    if (proxyBase) {
      return ["mobile", "desktop"].map((strategy) => {
        const endpoint = new URL(proxyBase);
        endpoint.searchParams.set("url", normalizedUrl);
        endpoint.searchParams.set("strategy", strategy);
        return Object.freeze({ strategy, url: endpoint.href });
      });
    }

    const key = String(apiKey ?? "").trim();
    if (!key) return [];

    return ["mobile", "desktop"].map((strategy) => {
      const endpoint = new URL(pageSpeedEndpoint);
      endpoint.searchParams.set("url", normalizedUrl);
      endpoint.searchParams.set("strategy", strategy);
      for (const category of gradeCategories) {
        endpoint.searchParams.append("category", category);
      }
      endpoint.searchParams.set("key", key);
      return Object.freeze({ strategy, url: endpoint.href });
    });
  }

  function prepareSiteGrade({
    input,
    apiKey = "",
    auditPath = "/pages/free-audit"
  }) {
    const normalized = normalizeWebsiteUrl(input);
    if (!normalized.valid) {
      return {
        mode: "invalid",
        message: normalized.error,
        requests: []
      };
    }

    const auditHref = buildContextUrl(`${auditPath}#human-audit`, {
      url: normalized.url,
      source: "site-grade"
    });
    const requests = buildPageSpeedRequests(normalized.url, apiKey);
    if (requests.length === 0) {
      return {
        mode: "human-audit",
        normalizedUrl: normalized.url,
        auditHref,
        requests,
        message: "An automated grade is unavailable right now. Your website address is ready for the free human audit."
      };
    }

    return {
      mode: "automated",
      normalizedUrl: normalized.url,
      auditHref,
      requests,
      message: "Google is testing your site. This can take 15 to 30 seconds."
    };
  }

  function packageForGoal(goal, config) {
    const packageName = config.estimatorRules.goalPackages[goal];
    const packageItem = config.packages.find((item) => item.name === packageName);
    if (!packageItem) throw new TypeError("Choose a valid site goal.");
    return packageItem;
  }

  function estimatorReason(answers, packageItem, addOns, config) {
    const goalReason = config.estimatorRules.goalReasons[answers.goal];
    const industry = config.estimatorRules.industryFocus[answers.businessType]
      ?? config.estimatorRules.industryFocus.other;
    const timing = config.estimatorRules.launchTimingLabels[answers.launchTiming]
      ?? config.estimatorRules.launchTimingLabels.flexible;
    const scopeReason = addOns.length > 0
      ? `${addOns.map((item) => item.label).join(" and ")} need separate scope.`
      : "Your page count and content readiness fit the base scope.";
    return [
      `${packageItem.name} fits because ${goalReason} For this business type, ${industry.reasoning}`,
      `${scopeReason} ${timing}`
    ];
  }

  function estimateProject(rawAnswers, config) {
    const answers = {
      businessType: String(rawAnswers?.businessType ?? ""),
      goal: String(rawAnswers?.goal ?? ""),
      pages: Math.max(1, Math.round(Number(rawAnswers?.pages) || 1)),
      contentReadiness: String(rawAnswers?.contentReadiness ?? ""),
      launchTiming: String(rawAnswers?.launchTiming ?? "")
    };
    const packageItem = packageForGoal(answers.goal, config);
    const addOns = [];
    const extraPages = Math.max(0, answers.pages - packageItem.pageAllowance);
    if (extraPages > 0) {
      addOns.push({
        id: "extra-pages",
        label: `${extraPages} extra ${extraPages === 1 ? "page" : "pages"}`,
        quantity: extraPages,
        unitPrice: config.estimatorRules.addOnRates.extraPage,
        amount: extraPages * config.estimatorRules.addOnRates.extraPage
      });
    }
    if (["partial", "not-ready"].includes(answers.contentReadiness)) {
      addOns.push({
        id: "copywriting-help",
        label: "Copywriting help",
        quantity: 1,
        unitPrice: config.estimatorRules.addOnRates.copywritingHelp,
        amount: config.estimatorRules.addOnRates.copywritingHelp
      });
    }

    const addOnTotal = addOns.reduce((total, item) => total + item.amount, 0);
    const standardTotal = packageItem.price + addOnTotal;
    /* PORT CHANGE 5 of 7: the founding rate REPLACES a package base price, so
       it may only apply to packages the owner marked eligible. Previously an
       open spot applied the founding rate to whichever package the estimator
       chose, which quoted the largest package at the entry founding rate. An
       absent or empty appliesTo list now means the offer applies to nothing,
       so a misconfiguration cannot silently under-quote. */
    const foundingEligible = Array.isArray(config.foundingOffer.appliesTo)
      ? config.foundingOffer.appliesTo.map((entry) => String(entry))
      : [];
    const spotOpen = Math.max(0, Number(config.foundingOffer.spotsRemaining) || 0) > 0;
    const offerApplied = spotOpen && foundingEligible.indexOf(packageItem.name) !== -1;
    const appliedBasePrice = offerApplied ? config.foundingOffer.price : packageItem.price;
    const estimatedTotal = appliedBasePrice + addOnTotal;

    const result = {
      answers,
      package: {
        name: packageItem.name,
        basePrice: packageItem.price,
        pricePrefix: packageItem.pricePrefix,
        timeline: packageItem.timeline,
        pageAllowance: packageItem.pageAllowance,
        inclusions: [...packageItem.inclusions]
      },
      addOns,
      industryFocus: [
        ...(config.estimatorRules.industryFocus[answers.businessType]
          ?? config.estimatorRules.industryFocus.other).inclusions
      ],
      addOnTotal,
      standardTotal,
      offerApplied,
      foundingBasePrice: offerApplied ? config.foundingOffer.price : null,
      estimatedTotal
    };
    result.reasoning = estimatorReason(answers, packageItem, addOns, config);
    return result;
  }

  function estimatorSummary(estimate) {
    const additions = estimate.addOns.length > 0
      ? estimate.addOns.map((item) => `${item.label}: $${item.amount}`).join(", ")
      : "None";
    const offer = estimate.offerApplied
      ? `Founding base rate applied: $${estimate.foundingBasePrice}`
      : "Standard base rate applies";
    return [
      "Estimated project context",
      `Package: ${estimate.package.name}`,
      `Estimated total: $${estimate.estimatedTotal}`,
      `Standard total: $${estimate.standardTotal}`,
      offer,
      `Business type: ${estimate.answers.businessType}`,
      `Pages: ${estimate.answers.pages}`,
      `Additions: ${additions}`,
      `Launch: ${estimate.reasoning[1].split(". ").at(-1)}`
    ].join("\n");
  }

  function buildEstimatorHandoff(estimate, config) {
    const context = {
      source: "project-estimator",
      package: estimate.package.name,
      estimate: estimate.estimatedTotal,
      businessType: estimate.answers.businessType,
      goal: estimate.answers.goal,
      pages: estimate.answers.pages,
      content: estimate.answers.contentReadiness,
      launch: estimate.answers.launchTiming,
      addOns: estimate.addOns.map((item) => item.id).join(",")
    };
    const auditHref = buildContextUrl("/pages/free-audit#human-audit", context);
    const summary = estimatorSummary(estimate);
    const scheduler = String(
      config.integrations.schedulerUrl ?? config.integrations.callUrl ?? ""
    ).trim();
    const emailHref = `mailto:${config.business.email}?${new URLSearchParams({
      subject: `${estimate.package.name} project estimate`,
      body: `${summary}\n\nPlease send me a copy of this estimate.`
    })}`;
    const callHref = scheduler
      ? buildContextUrl(scheduler, context)
      : `mailto:${config.business.email}?${new URLSearchParams({
        subject: `${estimate.package.name} project estimate`,
        body: `${summary}\n\nI would like to arrange a 15-minute call.`
      })}`;

    return { auditHref, callHref, emailHref, summary };
  }

  function checkedNumber(value, label, bounds) {
    const number = Number(value);
    if (!Number.isFinite(number) || number < bounds.min || number > bounds.max) {
      throw new TypeError(`${label} must be between ${bounds.min} and ${bounds.max}.`);
    }
    return number;
  }

  function interpolateCurve(seconds, points) {
    const ordered = [...points].sort((left, right) => left.seconds - right.seconds);
    if (seconds <= ordered[0].seconds) return ordered[0];
    if (seconds >= ordered.at(-1).seconds) return ordered.at(-1);
    const upperIndex = ordered.findIndex((point) => point.seconds >= seconds);
    const lower = ordered[upperIndex - 1];
    const upper = ordered[upperIndex];
    const span = upper.seconds - lower.seconds;
    const progress = span === 0 ? 0 : (seconds - lower.seconds) / span;
    const value = (key) => lower[key] + ((upper[key] - lower[key]) * progress);
    return {
      seconds,
      recoveryLow: value("recoveryLow"),
      recoveryHigh: value("recoveryHigh"),
      abandonmentLow: value("abandonmentLow"),
      abandonmentHigh: value("abandonmentHigh")
    };
  }

  function rounded(value, step) {
    if (value === 0) return 0;
    return Math.round(value / step) * step;
  }

  function calculateSlowSiteCost(rawInputs, config) {
    const curve = config.costCalculatorCurve;
    const visitors = checkedNumber(
      rawInputs?.visitors,
      "Monthly visitors",
      curve.inputBounds.visitors
    );
    const loadTime = checkedNumber(
      rawInputs?.loadTime,
      "Load time",
      curve.inputBounds.loadTime
    );
    const customerValue = checkedNumber(
      rawInputs?.customerValue,
      "Customer value",
      curve.inputBounds.customerValue
    );
    const notSure = String(rawInputs?.conversionRate) === "not-sure";
    const conversionRateRange = notSure
      ? { ...curve.typicalConversionRate }
      : (() => {
        const percent = checkedNumber(
          rawInputs?.conversionRate,
          "Conversion rate",
          curve.inputBounds.conversionRate
        );
        return { low: percent / 100, high: percent / 100 };
      })();
    const point = interpolateCurve(loadTime, curve.points);
    const visitorRange = {
      low: rounded(visitors * point.abandonmentLow, curve.rounding.visitors),
      high: rounded(visitors * point.abandonmentHigh, curve.rounding.visitors)
    };
    const currentConversions = {
      low: visitors * conversionRateRange.low,
      high: visitors * conversionRateRange.high
    };
    const revenueRange = {
      low: rounded(
        currentConversions.low * point.recoveryLow * customerValue,
        curve.rounding.revenue
      ),
      high: rounded(
        currentConversions.high * point.recoveryHigh * customerValue,
        curve.rounding.revenue
      )
    };
    const packageItem = config.packages.find((item) => item.name === curve.packageName);
    if (!packageItem) throw new TypeError("The calculator package is not configured.");
    const paybackMonths = revenueRange.low > 0 && revenueRange.high > 0
      ? {
        low: Math.max(
          curve.rounding.months,
          rounded(packageItem.price / revenueRange.high, curve.rounding.months)
        ),
        high: Math.max(
          curve.rounding.months,
          rounded(packageItem.price / revenueRange.low, curve.rounding.months)
        )
      }
      : null;

    return {
      inputs: { visitors, loadTime, customerValue },
      conversionRateRange,
      visitorsLost: visitorRange,
      revenueRange,
      paybackMonths,
      packageName: packageItem.name,
      packagePrice: packageItem.price,
      directional: true
    };
  }

  function configuredId(value, collection, fallback) {
    const ids = collection.map((item) => typeof item === "string" ? item : item.id);
    return ids.includes(String(value ?? "")) ? String(value) : fallback;
  }

  function normalizeMockupSelection(rawSelection, config) {
    const studio = config.mockupStudio;
    return {
      industry: configuredId(
        rawSelection?.industry,
        config.industries,
        studio.defaults.industry
      ),
      mode: configuredId(rawSelection?.mode, studio.modes, studio.defaults.mode),
      accent: configuredId(rawSelection?.accent, studio.accents, studio.defaults.accent),
      layout: configuredId(rawSelection?.layout, studio.layouts, studio.defaults.layout),
      device: configuredId(rawSelection?.device, studio.devices, studio.defaults.device)
    };
  }

  function randomMockupSelection(config, randomValue = Math.random()) {
    const states = config.mockupStudio.randomStates;
    const bounded = Math.min(0.999999, Math.max(0, Number(randomValue) || 0));
    return { ...states[Math.floor(bounded * states.length)] };
  }

  function buildMockupSearch(rawSelection, config) {
    const selected = normalizeMockupSelection(rawSelection, config);
    return `?${new URLSearchParams({
      mi: selected.industry,
      mm: selected.mode,
      ma: selected.accent,
      ml: selected.layout,
      md: selected.device
    })}`;
  }

  function mockupSelectionFromSearch(search, config) {
    const params = new URLSearchParams(String(search ?? "").replace(/^\?/, ""));
    return normalizeMockupSelection({
      industry: params.get("mi"),
      mode: params.get("mm"),
      accent: params.get("ma"),
      layout: params.get("ml"),
      device: params.get("md")
    }, config);
  }

  function buildMockupHandoff(rawSelection, config) {
    const selected = normalizeMockupSelection(rawSelection, config);
    const industry = config.industries.find((item) => item.id === selected.industry);
    const accent = config.mockupStudio.accents.find((item) => item.id === selected.accent);
    const layout = config.mockupStudio.layouts.find((item) => item.id === selected.layout);
    const body = [
      "Sample concept only",
      `Industry: ${industry.label}`,
      `Mode: ${selected.mode[0].toUpperCase()}${selected.mode.slice(1)}`,
      `Accent: ${accent.label}`,
      `Layout: ${layout.label}`,
      `Device view: ${selected.device}`
    ].join("\n");
    return {
      selection: selected,
      href: `mailto:${config.business.email}?${new URLSearchParams({
        subject: `Mockup Studio concept: ${industry.label}`,
        body: `${body}\n\nI would like to discuss a custom direction for my business.`
      })}`
    };
  }

  function normalizeHexColor(value) {
    const match = String(value ?? "").trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (!match) return null;
    const expanded = match[1].length === 3
      ? [...match[1]].map((character) => character.repeat(2)).join("")
      : match[1];
    return `#${expanded.toUpperCase()}`;
  }

  function relativeLuminance(hex) {
    const channels = hex.slice(1).match(/.{2}/g).map((value) => parseInt(value, 16) / 255);
    const linear = channels.map((channel) => (
      channel <= 0.04045
        ? channel / 12.92
        : ((channel + 0.055) / 1.055) ** 2.4
    ));
    return (0.2126 * linear[0]) + (0.7152 * linear[1]) + (0.0722 * linear[2]);
  }

  function contrastResult(foregroundValue, backgroundValue) {
    const foreground = normalizeHexColor(foregroundValue);
    const background = normalizeHexColor(backgroundValue);
    if (!foreground || !background) {
      throw new TypeError("Enter a valid 3 or 6 digit hex color beginning with #.");
    }
    const luminances = [relativeLuminance(foreground), relativeLuminance(background)];
    const ratio = (Math.max(...luminances) + 0.05) / (Math.min(...luminances) + 0.05);
    const roundedRatio = Math.round(ratio * 100) / 100;
    const status = {
      normalAA: ratio >= 4.5 ? "Pass" : "Fail",
      normalAAA: ratio >= 7 ? "Pass" : "Fail",
      largeAA: ratio >= 3 ? "Pass" : "Fail",
      largeAAA: ratio >= 4.5 ? "Pass" : "Fail"
    };
    const suggestion = status.normalAA === "Pass"
      ? "This pair meets normal text AA contrast. This is one check, not a complete accessibility audit."
      : "Try a darker text color or lighter background, then recheck. This is one contrast check, not a complete accessibility audit.";
    return { foreground, background, ratio: roundedRatio, status, suggestion };
  }

  function buildContrastSearch(rawState) {
    const foreground = normalizeHexColor(rawState?.foreground);
    const background = normalizeHexColor(rawState?.background);
    if (!foreground || !background) {
      throw new TypeError("Both share colors must be valid hex values.");
    }
    return `?${new URLSearchParams({ fg: foreground, bg: background })}`;
  }

  function contrastStateFromSearch(search, config) {
    const params = new URLSearchParams(String(search ?? "").replace(/^\?/, ""));
    return {
      foreground: normalizeHexColor(params.get("fg"))
        ?? config.contrastChecker.defaults.foreground,
      background: normalizeHexColor(params.get("bg"))
        ?? config.contrastChecker.defaults.background
    };
  }

  function buildHumanAuditMailto(rawFields, businessEmail) {
    const normalized = normalizeWebsiteUrl(rawFields?.url);
    if (!normalized.valid) {
      throw new TypeError(normalized.error);
    }

    const fields = {
      name: String(rawFields?.name ?? "").trim(),
      business: String(rawFields?.business ?? "").trim(),
      email: String(rawFields?.email ?? "").trim(),
      phone: String(rawFields?.phone ?? "").trim(),
      frustration: String(rawFields?.frustration ?? "").trim(),
      grade: String(rawFields?.grade ?? "").trim(),
      source: String(rawFields?.source ?? "").trim(),
      estimate: String(rawFields?.estimate ?? "").trim(),
      package: String(rawFields?.package ?? "").trim()
    };
    const body = [
      `Name: ${fields.name}`,
      `Business: ${fields.business}`,
      `Website: ${normalized.url}`,
      `Email: ${fields.email}`,
      fields.phone ? `Phone: ${fields.phone}` : "",
      fields.frustration ? `Biggest frustration: ${fields.frustration}` : "",
      fields.grade ? `Automated grade: ${fields.grade}` : "",
      fields.source ? `Source: ${fields.source}` : "",
      fields.estimate ? `Estimator total: $${fields.estimate}` : "",
      fields.package ? `Estimator package: ${fields.package}` : ""
    ].filter(Boolean).join("\n");
    const query = new URLSearchParams({
      subject: `Free human audit request: ${fields.business}`,
      body
    });

    return {
      href: `mailto:${String(businessEmail ?? "").trim()}?${query}`,
      normalizedUrl: normalized.url
    };
  }

  function pageSpeedResult(response, strategy, rubric, presentation) {
    const categories = response?.lighthouseResult?.categories ?? {};
    const scores = {
      performance: Math.round(Number(categories.performance?.score) * 100),
      accessibility: Math.round(Number(categories.accessibility?.score) * 100),
      seo: Math.round(Number(categories.seo?.score) * 100),
      bestPractices: Math.round(Number(categories["best-practices"]?.score) * 100)
    };
    const audits = response?.lighthouseResult?.audits ?? {};
    const vital = (id) => {
      const score = Number.isFinite(Number(audits[id]?.score))
        ? Number(audits[id].score)
        : null;
      const statusKey = score === null ? "unavailable" : score >= 0.9 ? "pass" : "needsReview";
      return {
        value: audits[id]?.displayValue ?? "Not available",
        score,
        status: {
          pass: "Pass",
          needsReview: "Needs review",
          unavailable: "Not reported"
        }[statusKey],
        meaning: presentation.vitalStatusMeanings[statusKey]
      };
    };
    const opportunities = Object.entries(audits)
      .filter(([, audit]) => audit?.details?.type === "opportunity" && Number(audit.numericValue) > 0)
      .sort(([, left], [, right]) => Number(right.numericValue) - Number(left.numericValue))
      .slice(0, 3)
      .map(([id]) => presentation.opportunityCopy[id] ?? presentation.opportunityFallback);
    const categoryDetails = gradeScoreKeys.map((id) => ({
      id,
      score: scores[id],
      explanation: presentation.categoryExplanations[id]
    }));

    return {
      strategy,
      scores,
      categories: categoryDetails,
      grade: calculateGrade(scores, rubric),
      vitals: {
        lcp: vital("largest-contentful-paint"),
        cls: vital("cumulative-layout-shift"),
        inp: vital("interaction-to-next-paint")
      },
      opportunities,
      performanceConsequence: { ...presentation.performanceConsequence }
    };
  }

  function comparisonPercent(value, min = 0, max = 100) {
    const numericValue = Number(value);
    const numericMin = Number(min);
    const numericMax = Number(max);
    if (
      !Number.isFinite(numericValue)
      || !Number.isFinite(numericMin)
      || !Number.isFinite(numericMax)
      || numericMax <= numericMin
    ) {
      throw new TypeError("Comparison values require a finite range with a larger maximum.");
    }
    const percent = ((numericValue - numericMin) / (numericMax - numericMin)) * 100;
    return Math.min(100, Math.max(0, percent));
  }

  function comparisonClip(percent) {
    const clamped = Math.min(100, Math.max(0, Number(percent) || 0));
    return `inset(0 ${100 - clamped}% 0 0)`;
  }

  function configuredChecklistType(siteType, checklistData) {
    const normalized = String(siteType ?? "").trim().toLowerCase();
    const match = checklistData?.siteTypes?.find((item) => item.id === normalized);
    if (!match) throw new TypeError("Choose a valid site type.");
    return match;
  }

  function checklistForType(siteType, checklistData) {
    const type = configuredChecklistType(siteType, checklistData);
    return checklistData.phases
      .map((phase) => ({
        id: phase.id,
        phase: phase.label,
        items: checklistData.items
          .filter((item) => item.phase === phase.id && item.siteTypes.includes(type.id))
          .map((item) => ({
            id: item.id,
            label: item.label,
            why: item.why
          }))
      }))
      .filter((group) => group.items.length > 0);
  }

  function checklistProgress(groups, checkedIds = []) {
    const items = groups.flatMap((group) => group.items);
    const configuredIds = new Set(items.map((item) => item.id));
    const checked = new Set(
      [...checkedIds].map(String).filter((id) => configuredIds.has(id))
    );
    const total = items.length;
    const complete = checked.size;
    return {
      complete,
      total,
      percent: total === 0 ? 0 : Math.round((complete / total) * 100)
    };
  }

  function checklistStorageKey(siteType, checklistData) {
    const type = configuredChecklistType(siteType, checklistData);
    const namespace = String(checklistData.storageNamespace || "upstream.launch-checklist");
    const version = Number(checklistData.version) || 1;
    return `${namespace}.v${version}:${type.id}`;
  }

  function checklistTextExport(siteType, groups, checkedIds = []) {
    const checked = new Set([...checkedIds].map(String));
    const typeLabel = String(siteType ?? "")
      .trim()
      .replace(/^\w/, (letter) => letter.toUpperCase());
    const progress = checklistProgress(groups, checked);
    const lines = [
      "Upstream Web Designs Launch Checklist",
      `Site type: ${typeLabel}`,
      `Progress: ${progress.complete} of ${progress.total} complete`,
      ""
    ];
    for (const group of groups) {
      lines.push(group.phase);
      for (const item of group.items) {
        lines.push(`${checked.has(item.id) ? "[x]" : "[ ]"} ${item.label}`);
        lines.push(`    Why: ${item.why}`);
      }
      lines.push("");
    }
    return lines.join("\n").trimEnd();
  }

  function dateOnlyParts(value) {
    const text = String(value ?? "").trim();
    const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) throw new TypeError("Enter a start date in YYYY-MM-DD format.");
    const [, yearText, monthText, dayText] = match;
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);
    const date = new Date(Date.UTC(year, month - 1, day, 12));
    if (
      date.getUTCFullYear() !== year
      || date.getUTCMonth() !== month - 1
      || date.getUTCDate() !== day
    ) {
      throw new TypeError("Enter a real calendar date in YYYY-MM-DD format.");
    }
    return { date };
  }

  function dateOnlyString(date) {
    return [
      String(date.getUTCFullYear()).padStart(4, "0"),
      String(date.getUTCMonth() + 1).padStart(2, "0"),
      String(date.getUTCDate()).padStart(2, "0")
    ].join("-");
  }

  function configuredWorkingDays(workingDays) {
    const days = [...new Set((workingDays ?? []).map(Number))];
    if (
      days.length === 0
      || days.some((day) => !Number.isInteger(day) || day < 0 || day > 6)
    ) {
      throw new TypeError("At least one valid working day is required.");
    }
    return new Set(days);
  }

  function normalizeStartDate(value, workingDays) {
    const { date } = dateOnlyParts(value);
    const allowed = configuredWorkingDays(workingDays);
    while (!allowed.has(date.getUTCDay())) {
      date.setUTCDate(date.getUTCDate() + 1);
    }
    return dateOnlyString(date);
  }

  function addWorkingDays(startDate, offset, workingDays) {
    if (!Number.isInteger(offset) || offset < 0) {
      throw new TypeError("Milestone offsets must be whole working days.");
    }
    const { date } = dateOnlyParts(startDate);
    const allowed = configuredWorkingDays(workingDays);
    let remaining = offset;
    while (remaining > 0) {
      date.setUTCDate(date.getUTCDate() + 1);
      if (allowed.has(date.getUTCDay())) remaining -= 1;
    }
    return dateOnlyString(date);
  }

  function calendarDateLabel(value) {
    const { date } = dateOnlyParts(value);
    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC"
    }).format(date);
  }

  function scheduleTimeline(packageName, startDate, timelineTemplates) {
    const requestedStart = dateOnlyString(dateOnlyParts(startDate).date);
    const normalizedName = String(packageName ?? "").trim().toLowerCase();
    const template = timelineTemplates?.packages?.find(
      (item) => item.packageName.toLowerCase() === normalizedName
    );
    if (!template) throw new TypeError("Choose a configured package.");
    const normalizedStart = normalizeStartDate(
      requestedStart,
      timelineTemplates.workingDays
    );
    return {
      packageName: template.packageName,
      requestedStart,
      startDate: normalizedStart,
      adjustedToWorkingDay: requestedStart !== normalizedStart,
      milestones: template.milestones.map((milestone) => {
        const date = addWorkingDays(
          normalizedStart,
          milestone.workingDayOffset,
          timelineTemplates.workingDays
        );
        return {
          id: milestone.id,
          label: milestone.label,
          workingDayOffset: milestone.workingDayOffset,
          date,
          weekday: calendarDateLabel(date).split(",")[0]
        };
      })
    };
  }

  function compactIcsDate(value) {
    return dateOnlyString(dateOnlyParts(value).date).replaceAll("-", "");
  }

  function nextCalendarDate(value) {
    const { date } = dateOnlyParts(value);
    date.setUTCDate(date.getUTCDate() + 1);
    return dateOnlyString(date);
  }

  function icsEscape(value) {
    return String(value)
      .replaceAll("\\", "\\\\")
      .replaceAll("\r\n", "\\n")
      .replaceAll("\n", "\\n")
      .replaceAll(",", "\\,")
      .replaceAll(";", "\\;");
  }

  function icsTimestamp(value) {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) throw new TypeError("A valid generated time is required.");
    return date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
  }

  function buildTimelineIcs(schedule, options = {}) {
    const calendarName = options.calendarName || "Upstream Project Plan";
    const stamp = icsTimestamp(options.generatedAt ?? new Date());
    const lines = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "PRODID:-//Upstream Web Designs//Project Timeline Planner//EN",
      `X-WR-CALNAME:${icsEscape(calendarName)}`
    ];
    for (const milestone of schedule.milestones) {
      lines.push(
        "BEGIN:VEVENT",
        `UID:${milestone.date}-${milestone.id}@upstreamwebdesigns.com`,
        `DTSTAMP:${stamp}`,
        `DTSTART;VALUE=DATE:${compactIcsDate(milestone.date)}`,
        `DTEND;VALUE=DATE:${compactIcsDate(nextCalendarDate(milestone.date))}`,
        `SUMMARY:${icsEscape(milestone.label)}`,
        `DESCRIPTION:${icsEscape(`${schedule.packageName} planning milestone. Dates remain informational until confirmed.`)}`,
        "TRANSP:TRANSPARENT",
        "END:VEVENT"
      );
    }
    lines.push("END:VCALENDAR");
    return `${lines.join("\r\n")}\r\n`;
  }

  { gradeCategories, pageSpeedEndpoint };


  const configNode = document.querySelector("#site-tools-config");
  const config = configNode ? JSON.parse(configNode.textContent) : null;

  /* PORT CHANGE 3 of 7: hand the proxy address to the request builder. */
  upstreamProxyBase = (config && config.integrations && config.integrations.psiProxyUrl) || "";

  function track(eventName, props = {}) {
    if (typeof window.track === "function") {
      window.track(eventName, props);
      return;
    }
    window.dispatchEvent(new CustomEvent("upstream:track", {
      detail: { eventName, props }
    }));
  }

  function empty(node) {
    while (node.firstChild) node.firstChild.remove();
  }

  function create(tagName, className = "", text = "") {
    const node = document.createElement(tagName);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function createLink(label, href, className = "button button-secondary") {
    const link = create("a", className, label);
    link.href = href;
    return link;
  }

  function showMessage(result, message, className = "") {
    empty(result);
    result.className = `tool-result ${className}`.trim();
    result.append(create("p", "", message));
  }

  function storedJson(storage, key, fallback) {
    try {
      const parsed = JSON.parse(storage.getItem(key) ?? "null");
      return parsed ?? fallback;
    } catch {
      return fallback;
    }
  }

  function setStoredJson(storage, key, value) {
    try {
      storage.setItem(key, JSON.stringify(value));
    } catch {
      return;
    }
  }

  function removeStored(storage, key) {
    try {
      storage.removeItem(key);
    } catch {
      return;
    }
  }

  function downloadFile(filename, type, content) {
    const url = URL.createObjectURL(new Blob([content], { type }));
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.hidden = true;
    document.body.append(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  function printTool(id) {
    document.body.dataset.printTool = id;
    window.addEventListener("afterprint", () => {
      delete document.body.dataset.printTool;
    }, { once: true });
    window.print();
  }

  function auditFields() {
    return {
      url: document.querySelector("#human-audit-url"),
      grade: document.querySelector("#human-audit-grade"),
      source: document.querySelector("#human-audit-source"),
      estimate: document.querySelector("#human-audit-estimate"),
      package: document.querySelector("#human-audit-package"),
      summary: document.querySelector("[data-audit-context-summary]")
    };
  }

  function setAuditContext(context) {
    const fields = auditFields();
    for (const key of ["url", "grade", "source", "estimate", "package"]) {
      if (fields[key] && context[key]) fields[key].value = context[key];
    }
    if (!fields.summary) return;

    const details = [];
    if (context.url) details.push(`Website ready: ${context.url}`);
    if (context.grade) details.push(`Automated context: ${context.grade}`);
    if (context.package) details.push(`Estimator package: ${context.package}`);
    if (context.estimate) details.push(`Estimator total: $${context.estimate}`);
    fields.summary.textContent = details.join(" ");
  }

  function hydrateAuditFromQuery() {
    if (!document.querySelector("#human-audit")) return;
    const params = new URLSearchParams(window.location.search);
    const rawUrl = params.get("url") ?? "";
    const normalized = rawUrl ? normalizeWebsiteUrl(rawUrl) : null;
    setAuditContext({
      url: normalized?.valid ? normalized.url : rawUrl,
      grade: params.get("grade") ?? "",
      source: params.get("source") ?? "",
      estimate: params.get("estimate") ?? "",
      package: params.get("package") ?? ""
    });
  }

  function renderAuditHandoff(resultNode, gradeState) {
    empty(resultNode);
    resultNode.className = "tool-result tool-result-card";
    resultNode.append(create("p", "tool-result-title", "The human audit is ready."));
    resultNode.append(create("p", "", gradeState.message));
    resultNode.append(createLink("Continue with the free human audit", gradeState.auditHref));
    setAuditContext({
      url: gradeState.normalizedUrl,
      source: "site-grade"
    });
  }

  function categoryLabel(category) {
    return {
      performance: "Performance",
      accessibility: "Accessibility",
      seo: "SEO",
      bestPractices: "Best practices"
    }[category];
  }

  function renderStrategyScore(result) {
    const section = create("section", "grade-strategy");
    const heading = create("h4");
    heading.append(document.createTextNode(
      `${result.strategy === "mobile" ? "Mobile" : "Desktop"}: ${result.grade.letter}, `
    ));
    const gradeScore = create("span", "grade-score-value", String(result.grade.score));
    gradeScore.dataset.motionCount = String(result.grade.score);
    heading.append(gradeScore);
    heading.append(document.createTextNode(" of 100"));
    section.append(heading);

    const scoreGrid = create("div", "score-grid");
    for (const category of result.categories) {
      const card = create("div", "score-card");
      card.dataset.scoreCategory = category.id;
      card.append(create("span", "score-label", categoryLabel(category.id)));
      const score = create("strong", "score-value", String(category.score));
      score.dataset.motionCount = String(category.score);
      card.append(score);
      card.append(create("p", "score-explanation", category.explanation));
      scoreGrid.append(card);
    }
    section.append(scoreGrid);

    const vitalList = create("ul", "vital-list");
    for (const [key, vital] of Object.entries(result.vitals)) {
      const item = create("li");
      item.append(create("strong", "", `${key.toUpperCase()}: ${vital.value}, ${vital.status}`));
      item.append(create("span", "vital-meaning", vital.meaning));
      vitalList.append(item);
    }
    section.append(vitalList);

    const consequence = create("p", "performance-consequence", result.performanceConsequence.text);
    consequence.append(document.createTextNode(" "));
    consequence.append(createLink(
      result.performanceConsequence.sourceTitle,
      result.performanceConsequence.sourceUrl,
      "text-link"
    ));
    section.append(consequence);

    if (result.opportunities.length > 0) {
      section.append(create("h5", "", "Top opportunities"));
      const list = create("ol", "opportunity-list");
      for (const opportunity of result.opportunities) {
        list.append(create("li", "", opportunity));
      }
      section.append(list);
    }
    return section;
  }

  function renderGradeScorecard(resultNode, gradeState, results) {
    empty(resultNode);
    resultNode.className = "tool-result tool-result-card grade-scorecard";
    resultNode.append(create("p", "tool-result-title", "Google PageSpeed results"));
    for (const result of results) {
      resultNode.append(renderStrategyScore(result));
    }
    const gradeContext = results
      .map((result) => `${result.strategy === "mobile" ? "Mobile" : "Desktop"} ${result.grade.letter} (${result.grade.score})`)
      .join(", ");
    const auditHref = buildContextUrl(gradeState.auditHref, { grade: gradeContext });
    resultNode.append(createLink("Get the free human audit", auditHref));
    setAuditContext({
      url: gradeState.normalizedUrl,
      grade: gradeContext,
      source: "site-grade"
    });
    document.dispatchEvent(new CustomEvent("upstream:numericresults", {
      detail: { root: resultNode }
    }));
  }

  const siteGradeRequestGenerations = new WeakMap();

  async function runSiteGrade(form) {
    const resultNode = form.querySelector('[data-tool-result="site-grade"]');
    const input = form.elements.url;
    const requestGeneration = (siteGradeRequestGenerations.get(form) ?? 0) + 1;
    siteGradeRequestGenerations.set(form, requestGeneration);
    const gradeState = prepareSiteGrade({
      input: input.value,
      apiKey: config.integrations.pageSpeedApiKey,
      auditPath: "/pages/free-audit"
    });

    input.setCustomValidity("");
    input.removeAttribute("aria-invalid");
    if (gradeState.mode === "invalid") {
      input.setCustomValidity(gradeState.message);
      input.setAttribute("aria-invalid", "true");
      showMessage(resultNode, gradeState.message, "tool-error");
      input.focus();
      track("site_grade_invalid");
      return;
    }

    input.value = gradeState.normalizedUrl;
    if (gradeState.mode === "human-audit") {
      renderAuditHandoff(resultNode, gradeState);
      track("site_grade_human_handoff", { url: gradeState.normalizedUrl });
      return;
    }

    showMessage(resultNode, gradeState.message, "tool-loading");
    form.setAttribute("aria-busy", "true");
    const submit = form.querySelector('button[type="submit"]');
    submit.disabled = true;
    try {
      const payloads = await Promise.all(gradeState.requests.map(async (request) => {
        const response = await fetch(request.url, { cache: "no-store" });
        if (!response.ok) throw new Error(`PageSpeed request failed with ${response.status}.`);
        const payload = await response.json();
        if (payload.error) throw new Error("PageSpeed returned an error.");
        return pageSpeedResult(
          payload,
          request.strategy,
          config.gradeRubric,
          config.gradePresentation
        );
      }));
      if (siteGradeRequestGenerations.get(form) !== requestGeneration) return;
      renderGradeScorecard(resultNode, gradeState, payloads);
      track("site_grade_complete", { url: gradeState.normalizedUrl });
    } catch {
      if (siteGradeRequestGenerations.get(form) !== requestGeneration) return;
      renderAuditHandoff(resultNode, {
        ...gradeState,
        message: "The automated grade is unavailable right now. Your website address is saved for the free human audit."
      });
      resultNode.classList.add("tool-error");
      track("site_grade_failed", { url: gradeState.normalizedUrl });
    } finally {
      if (siteGradeRequestGenerations.get(form) === requestGeneration) {
        form.removeAttribute("aria-busy");
        submit.disabled = false;
      }
    }
  }

  function money(value) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0
    }).format(value);
  }

  function appendList(parent, heading, items) {
    parent.append(create("h4", "", heading));
    const list = create("ul", "estimate-list");
    for (const item of items) list.append(create("li", "", item));
    parent.append(list);
  }

  function renderEstimator(resultNode, estimate, handoff) {
    empty(resultNode);
    resultNode.className = "tool-result tool-result-card estimator-result";
    resultNode.append(create("p", "tool-result-title", "My recommendation, and why:"));
    resultNode.append(create("h3", "", `${estimate.package.name}: ${money(estimate.estimatedTotal)} estimated`));
    if (estimate.offerApplied) {
      resultNode.append(create(
        "p",
        "offer-note",
        `The ${money(estimate.foundingBasePrice)} founding base rate is applied because a configured spot remains. The standard total is ${money(estimate.standardTotal)}.`
      ));
    } else {
      resultNode.append(create("p", "", `Standard rate estimate: ${money(estimate.standardTotal)}.`));
    }
    for (const reason of estimate.reasoning) resultNode.append(create("p", "", reason));
    resultNode.append(create("p", "", `Typical package timeline: ${estimate.package.timeline}.`));
    appendList(resultNode, "Included", estimate.package.inclusions);
    appendList(resultNode, "Focus for this business", estimate.industryFocus);
    appendList(
      resultNode,
      "Paid additions",
      estimate.addOns.length > 0
        ? estimate.addOns.map((item) => `${item.label}: ${money(item.amount)}`)
        : ["No paid additions flagged by these answers."]
    );

    const actions = create("div", "estimator-actions");
    actions.append(createLink("Start with the free audit", handoff.auditHref, "button button-primary"));
    actions.append(createLink(
      config.integrations.callUrl ? "Book a 15-minute call" : "Email me about a 15-minute call",
      handoff.callHref
    ));
    actions.append(createLink("Email me this estimate", handoff.emailHref, "text-link"));
    resultNode.append(actions);
  }

  function runEstimator(form) {
    const values = new FormData(form);
    const estimate = estimateProject({
      businessType: values.get("businessType"),
      goal: values.get("goal"),
      pages: values.get("pages"),
      contentReadiness: values.get("contentReadiness"),
      launchTiming: values.get("launchTiming")
    }, config);
    const handoff = buildEstimatorHandoff(estimate, config);
    renderEstimator(
      form.querySelector('[data-tool-result="project-estimator"]'),
      estimate,
      handoff
    );
    track("project_estimator_complete", {
      package: estimate.package.name,
      estimate: estimate.estimatedTotal
    });
  }

  function number(value) {
    return new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 1
    }).format(value);
  }

  function rangeText(range, formatter = number) {
    return `${formatter(range.low)} to ${formatter(range.high)}`;
  }

  function replaceToolSearch(search) {
    const next = new URL(window.location.href);
    const incoming = new URLSearchParams(search);
    for (const [key, value] of incoming) next.searchParams.set(key, value);
    history.replaceState(null, "", `${next.pathname}${next.search}${next.hash}`);
  }

  function runSlowSiteCost(form) {
    const values = new FormData(form);
    const resultNode = form.querySelector('[data-tool-result="slow-site-cost"]');
    try {
      const result = calculateSlowSiteCost({
        visitors: values.get("visitors"),
        loadTime: values.get("loadTime"),
        customerValue: values.get("customerValue"),
        conversionRate: values.get("conversionRate")
      }, config);
      resultNode.classList.remove("tool-error");
      resultNode.querySelector('[data-cost-output="visitors"]').textContent =
        `${rangeText(result.visitorsLost)} visitors`;
      resultNode.querySelector('[data-cost-output="revenue"]').textContent =
        rangeText(result.revenueRange, money);
      resultNode.querySelector('[data-cost-output="payback"]').textContent =
        result.paybackMonths
          ? `${rangeText(result.paybackMonths)} months for the ${money(result.packagePrice)} ${result.packageName} package`
          : "No payback range with zero estimated revenue";
      resultNode.querySelector('[data-cost-output="scope"]').textContent =
        config.costCalculatorCurve.disclaimer;
      track("slow_site_cost_complete", {
        visitors: result.inputs.visitors,
        loadTime: result.inputs.loadTime
      });
    } catch (error) {
      resultNode.classList.add("tool-error");
      resultNode.querySelector('[data-cost-output="scope"]').textContent = error.message;
    }
  }

  function updateCostInputOutput(form) {
    const visitors = form.querySelector('[data-cost-input-output="visitors"]');
    const loadTime = form.querySelector('[data-cost-input-output="loadTime"]');
    visitors.textContent = number(form.elements.visitors.value);
    loadTime.textContent = `${number(form.elements.loadTime.value)} seconds`;
  }

  function currentMockupSelection(form) {
    const values = new FormData(form);
    return {
      industry: values.get("industry"),
      mode: values.get("mode"),
      accent: values.get("accent"),
      layout: values.get("layout"),
      device: values.get("device")
    };
  }

  function setMockupControls(form, selection) {
    form.elements.industry.value = selection.industry;
    form.elements.layout.value = selection.layout;
    form.elements.accent.value = selection.accent;
    for (const radio of form.elements.mode) radio.checked = radio.value === selection.mode;
    for (const radio of form.elements.device) radio.checked = radio.value === selection.device;
  }

  function renderMockup(form, rawSelection, updateUrl = true) {
    const selection = mockupSelectionFromSearch(
      buildMockupSearch(rawSelection, config),
      config
    );
    setMockupControls(form, selection);
    const industry = config.industries.find((item) => item.id === selection.industry);
    const accent = config.mockupStudio.accents.find((item) => item.id === selection.accent);
    const preview = form.querySelector("[data-mockup-preview]");
    preview.dataset.mode = selection.mode;
    preview.dataset.layout = selection.layout;
    preview.dataset.device = selection.device;
    preview.style.setProperty("--mockup-accent", accent.value);
    form.querySelector("[data-mockup-name]").textContent = industry.name;
    form.querySelector("[data-mockup-hero]").textContent = industry.hero;
    form.querySelector("[data-mockup-booking]").textContent = industry.bookingBar;
    const services = form.querySelector("[data-mockup-services]");
    empty(services);
    for (const service of industry.services) services.append(create("li", "", service));
    const handoff = buildMockupHandoff(selection, config);
    form.querySelector("[data-mockup-contact]").href = handoff.href;
    form.querySelector('[data-tool-result="mockup-studio"]').textContent =
      `Sample ${industry.label.toLowerCase()} concept, ${selection.mode} mode, ${accent.label.toLowerCase()}, ${selection.layout} layout, ${selection.device} view.`;
    if (updateUrl) replaceToolSearch(buildMockupSearch(selection, config));
    return selection;
  }

  function setContrastControls(form, state) {
    form.elements.foreground.value = state.foreground;
    form.elements.foregroundPicker.value = state.foreground;
    form.elements.background.value = state.background;
    form.elements.backgroundPicker.value = state.background;
  }

  function runContrast(form, updateUrl = true) {
    const resultNode = form.querySelector('[data-tool-result="contrast-checker"]');
    for (const field of [form.elements.foreground, form.elements.background]) {
      field.setCustomValidity("");
      field.removeAttribute("aria-invalid");
    }
    try {
      const result = contrastResult(
        form.elements.foreground.value,
        form.elements.background.value
      );
      setContrastControls(form, result);
      form.querySelector("[data-contrast-preview]").style.cssText =
        `color:${result.foreground};background:${result.background}`;
      form.querySelector("[data-contrast-ratio]").textContent =
        `${result.ratio.toFixed(2)} to 1 contrast ratio`;
      for (const [key, status] of Object.entries(result.status)) {
        const node = form.querySelector(`[data-contrast-status="${key}"]`);
        node.textContent = status;
        node.dataset.status = status.toLowerCase();
      }
      form.querySelector("[data-contrast-suggestion]").textContent = result.suggestion;
      resultNode.classList.remove("tool-error");
      if (updateUrl) {
        replaceToolSearch(buildContrastSearch({
          foreground: result.foreground,
          background: result.background
        }));
      }
      track("contrast_check_complete", { ratio: result.ratio });
      return result;
    } catch (error) {
      resultNode.classList.add("tool-error");
      form.querySelector("[data-contrast-suggestion]").textContent = error.message;
      for (const field of [form.elements.foreground, form.elements.background]) {
        field.setCustomValidity(error.message);
        field.setAttribute("aria-invalid", "true");
      }
      return null;
    }
  }

  function updateComparison(root) {
    const range = root.querySelector('input[type="range"]');
    const percent = comparisonPercent(range.value, range.min, range.max);
    root.style.setProperty("--comparison-percent", `${percent}%`);
    root.querySelector("[data-comparison-after]").style.clipPath = comparisonClip(percent);
    root.querySelector("[data-comparison-value]").textContent = `${Math.round(percent)}% After`;
  }

  function initializeComparison(root) {
    updateComparison(root);
    const range = root.querySelector('input[type="range"]');
    range.addEventListener("input", () => updateComparison(root));
    const hint = root.closest(".comparison-tool")?.querySelector("[data-comparison-hint]");
    const sessionKey = "upstream.comparison-hint.v1";
    let alreadyShown = false;
    try {
      alreadyShown = sessionStorage.getItem(sessionKey) === "shown";
      if (!alreadyShown) sessionStorage.setItem(sessionKey, "shown");
    } catch {
      alreadyShown = false;
    }
    if (!alreadyShown && hint) {
      hint.hidden = false;
      root.classList.add("comparison-nudge");
    }
  }

  const checklistStates = new WeakMap();

  function checkedChecklistIds(form) {
    return [...form.querySelectorAll('input[name="check"]:checked')]
      .map((input) => input.value);
  }

  function renderChecklistProgress(form, save = true) {
    const state = checklistStates.get(form);
    if (!state) return;
    const checkedIds = checkedChecklistIds(form);
    const progress = checklistProgress(state.groups, checkedIds);
    const progressNode = form.querySelector("[data-checklist-progress]");
    progressNode.max = progress.total;
    progressNode.value = progress.complete;
    progressNode.textContent = `${progress.percent}%`;
    form.querySelector("[data-checklist-progress-text]").textContent =
      `${progress.complete} of ${progress.total} complete, ${progress.percent}%`;
    form.querySelector('[data-tool-result="launch-checklist"]').textContent =
      progress.complete === progress.total
        ? "All configured checks are marked complete. Review them before launch."
        : `Progress saved in this browser: ${progress.complete} of ${progress.total} complete.`;
    if (save) setStoredJson(localStorage, state.storageKey, checkedIds);
  }

  function renderChecklist(form) {
    const siteType = form.elements.siteType.value;
    const groups = checklistForType(siteType, config.checklistData);
    const storageKey = checklistStorageKey(siteType, config.checklistData);
    const checked = new Set(storedJson(localStorage, storageKey, []));
    const container = form.querySelector("[data-checklist-groups]");
    empty(container);
    for (const group of groups) {
      const fieldset = create("fieldset", "checklist-phase");
      fieldset.append(create("legend", "", group.phase));
      for (const item of group.items) {
        const label = create("label", "checklist-item");
        const input = create("input");
        input.type = "checkbox";
        input.name = "check";
        input.value = item.id;
        input.checked = checked.has(item.id);
        const copy = create("span");
        copy.append(create("strong", "", item.label));
        copy.append(create("small", "", item.why));
        label.append(input, copy);
        fieldset.append(label);
      }
      container.append(fieldset);
    }
    checklistStates.set(form, { siteType, groups, storageKey });
    renderChecklistProgress(form, false);
  }

  function initializeChecklist(form) {
    renderChecklist(form);
    form.elements.siteType.addEventListener("change", () => renderChecklist(form));
    form.addEventListener("input", (event) => {
      if (event.target.matches('input[name="check"]')) renderChecklistProgress(form);
    });
    form.querySelector("[data-checklist-reset]").addEventListener("click", () => {
      const state = checklistStates.get(form);
      removeStored(localStorage, state.storageKey);
      renderChecklist(form);
      form.querySelector('[data-tool-result="launch-checklist"]').textContent =
        "Checks reset for this site type.";
    });
    form.querySelector("[data-checklist-print]").addEventListener("click", () => {
      track("launch_checklist_print", { siteType: form.elements.siteType.value });
      printTool("launch-checklist");
    });
    form.querySelector("[data-checklist-download]").addEventListener("click", () => {
      const state = checklistStates.get(form);
      const text = checklistTextExport(
        state.siteType,
        state.groups,
        checkedChecklistIds(form)
      );
      downloadFile(
        `upstream-launch-checklist-${state.siteType}.txt`,
        "text/plain;charset=utf-8",
        text
      );
      track("launch_checklist_download", { siteType: state.siteType });
    });
  }

  const timelineStates = new WeakMap();

  function renderTimeline(form, schedule) {
    form.querySelector("[data-timeline-plan]").classList.add("is-ready");
    const list = form.querySelector("[data-timeline-milestones]");
    empty(list);
    for (const milestone of schedule.milestones) {
      const item = create("li");
      item.dataset.milestone = milestone.id;
      item.append(create("span", "", milestone.label));
      const time = create("time", "", calendarDateLabel(milestone.date));
      time.dateTime = milestone.date;
      item.append(time);
      list.append(item);
    }
    const adjustment = schedule.adjustedToWorkingDay
      ? ` The selected date falls outside configured working days, so kickoff moves to ${calendarDateLabel(schedule.startDate)}.`
      : "";
    form.querySelector("[data-timeline-summary]").textContent =
      `${schedule.packageName} plan starting ${calendarDateLabel(schedule.startDate)}.${adjustment}`;
  }

  function runTimeline(form) {
    const result = form.querySelector('[data-tool-result="timeline-planner"]');
    try {
      const schedule = scheduleTimeline(
        form.elements.package.value,
        form.elements.start.value,
        config.timelineTemplates
      );
      const ics = buildTimelineIcs(schedule, {
        calendarName: `${schedule.packageName} project plan`
      });
      timelineStates.set(form, { schedule, ics });
      renderTimeline(form, schedule);
      form.querySelector("[data-timeline-download]").disabled = false;
      result.classList.remove("tool-error");
      result.textContent = "Timeline ready. The calendar file is generated locally when you download it.";
      track("timeline_plan_generated", {
        package: schedule.packageName,
        startDate: schedule.startDate
      });
    } catch (error) {
      timelineStates.delete(form);
      form.querySelector("[data-timeline-download]").disabled = true;
      result.classList.add("tool-error");
      result.textContent = error.message;
      form.elements.start.focus();
    }
  }

  function initializeTimeline(form) {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      runTimeline(form);
    });
    form.querySelector("[data-timeline-print]").addEventListener("click", () => {
      printTool("timeline-planner");
    });
    form.querySelector("[data-timeline-download]").addEventListener("click", () => {
      const state = timelineStates.get(form);
      if (!state) {
        runTimeline(form);
        return;
      }
      downloadFile(
        `upstream-${state.schedule.packageName.toLowerCase()}-timeline.ics`,
        "text/calendar;charset=utf-8",
        state.ics
      );
      track("timeline_calendar_download", {
        package: state.schedule.packageName,
        startDate: state.schedule.startDate
      });
    });
  }

  /* The audit request used to have exactly one outcome: hand the visitor a mailto:
     link and hope they switched to their mail app and pressed send. That is the
     highest-intent moment on the site, and it was leaking every lead that did not
     want to do the studio's filing for it.

     With settings.form_endpoint configured, the form now posts to the Worker. Three
     layers, weakest assumption last:
       1. this fetch, which keeps the visitor on the page,
       2. the form's own action/method, which the browser uses if this script never
          runs at all,
       3. runHumanAuditFallback, if the endpoint is unreachable at submit time.
     Layer 3 is the reason a network failure still cannot lose a lead. */
  function markAuditReceived(form) {
    const status = form.querySelector("[data-form-status]");
    form.querySelectorAll("input, textarea, select, button").forEach((el) => {
      el.disabled = true;
    });
    form.setAttribute("data-audit-received", "");
    if (status) {
      status.textContent = "Received. I read the site myself and reply within one business day.";
    }
  }

  async function runHumanAuditPost(form, endpoint) {
    const status = form.querySelector("[data-form-status]");
    const submit = form.querySelector('button[type="submit"]');
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    const values = Object.fromEntries(new FormData(form));
    /* Normalize here as well as on the server so the address we store is the same
       one Site Grade measured, and so a typo is corrected in front of the visitor
       rather than silently on the way past. */
    const normalized = normalizeWebsiteUrl(values.url);
    if (!normalized.valid) {
      if (status) status.textContent = normalized.error;
      form.elements.url.setAttribute("aria-invalid", "true");
      form.elements.url.focus();
      return;
    }
    values.url = normalized.url;
    form.elements.url.value = normalized.url;
    form.elements.url.removeAttribute("aria-invalid");

    const restoreLabel = submit ? submit.textContent : "";
    if (submit) {
      submit.disabled = true;
      submit.textContent = "Sending";
    }
    if (status) status.textContent = "Sending your request.";

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values)
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.ok !== true) {
        const message = Array.isArray(payload.errors) && payload.errors.length
          ? payload.errors[0]
          : "That request could not be sent.";
        /* A 400 is the visitor's input, not a broken endpoint. Show it and let them
           correct it; falling back to a mailto here would paper over a real error. */
        if (response.status === 400) {
          if (submit) { submit.disabled = false; submit.textContent = restoreLabel; }
          if (status) status.textContent = message;
          return;
        }
        throw new Error(message);
      }
      markAuditReceived(form);
      track("human_audit_submitted", { url: values.url, source: values.source ?? "" });
    } catch (error) {
      if (submit) { submit.disabled = false; submit.textContent = restoreLabel; }
      if (status) {
        status.textContent = "That did not send. Opening a prepared email instead so nothing is lost.";
      }
      track("human_audit_post_failed", { url: values.url });
      runHumanAuditFallback(form);
    }
  }

  function runHumanAuditFallback(form) {
    const status = form.querySelector("[data-form-status]");
    const values = Object.fromEntries(new FormData(form));
    try {
      const handoff = buildHumanAuditMailto(values, config.business.email);
      form.elements.url.value = handoff.normalizedUrl;
      status.textContent = "Your email app should now have a prepared audit request. Review it, then send it when you are ready.";
      window.location.href = handoff.href;
      track("human_audit_email_prepared", {
        url: handoff.normalizedUrl,
        source: values.source ?? ""
      });
    } catch (error) {
      status.textContent = error.message;
      form.elements.url.setAttribute("aria-invalid", "true");
      form.elements.url.focus();
    }
  }

  /* PORT CHANGE 6 of 7: bind on the Shopify section lifecycle, not once at defer
     time, so tools keep working while the owner edits the page around them.

     upstreamUnbound returns only the nodes it has not already claimed, and marks
     them as it goes, which makes re-binding idempotent without threading a guard
     through every tool. */
  function upstreamUnbound(selector) {
    var nodes = [];
    document.querySelectorAll(selector).forEach(function (el) {
      if (el.hasAttribute("data-tool-bound")) return;
      el.setAttribute("data-tool-bound", "");
      nodes.push(el);
    });
    return nodes;
  }

  function upstreamBindTools() {
    if (!config) return;
    hydrateAuditFromQuery();
    upstreamUnbound('[data-tool="site-grade"]').forEach((form) => {
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        runSiteGrade(form);
      });
    });
    upstreamUnbound('[data-tool="project-estimator"]').forEach((form) => {
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        runEstimator(form);
      });
    });
    upstreamUnbound('[data-tool="slow-site-cost"]').forEach((form) => {
      updateCostInputOutput(form);
      form.addEventListener("input", () => updateCostInputOutput(form));
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        runSlowSiteCost(form);
      });
    });
    upstreamUnbound('[data-tool="mockup-studio"]').forEach((form) => {
      const initial = mockupSelectionFromSearch(window.location.search, config);
      renderMockup(form, initial, false);
      form.addEventListener("change", () => {
        renderMockup(form, currentMockupSelection(form));
      });
      form.querySelector("[data-mockup-randomize]").addEventListener("click", () => {
        const selected = renderMockup(form, randomMockupSelection(config));
        track("mockup_studio_randomized", selected);
      });
    });
    upstreamUnbound('[data-tool="contrast-checker"]').forEach((form) => {
      setContrastControls(form, contrastStateFromSearch(window.location.search, config));
      runContrast(form, false);
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        runContrast(form);
      });
      for (const name of ["foreground", "background"]) {
        /* PORT CHANGE 4 of 7: debounced, because this wrote into a live region
     on every keystroke. */
  var contrastTimer = null;
  form.elements[name].addEventListener("input", function () {
    window.clearTimeout(contrastTimer);
    contrastTimer = window.setTimeout(function () { runContrast(form); }, 250);
  });
        form.elements[`${name}Picker`].addEventListener("input", () => {
          form.elements[name].value = form.elements[`${name}Picker`].value.toUpperCase();
          runContrast(form);
        });
      }
      form.querySelector("[data-contrast-swap]").addEventListener("click", () => {
        const foreground = form.elements.foreground.value;
        form.elements.foreground.value = form.elements.background.value;
        form.elements.background.value = foreground;
        runContrast(form);
      });
      form.querySelector("[data-contrast-share]").addEventListener("click", async () => {
        const result = runContrast(form);
        if (!result) {
          form.elements.foreground.focus();
          return;
        }
        try {
          await navigator.clipboard?.writeText(window.location.href);
          form.querySelector("[data-contrast-suggestion]").textContent =
            "Shareable link copied. This is one contrast check, not a complete accessibility audit.";
        } catch {
          form.querySelector("[data-contrast-suggestion]").textContent =
            "The shareable colors are now in this page address. Copy it from the address bar.";
        }
      });
    });
    upstreamUnbound("[data-comparison]").forEach(initializeComparison);
    upstreamUnbound('[data-tool="launch-checklist"]').forEach(initializeChecklist);
    upstreamUnbound('[data-tool="timeline-planner"]').forEach(initializeTimeline);
    upstreamUnbound('[data-human-audit]').forEach((form) => {
      /* Stamp the fill clock on bind. The server discards anything submitted inside
         750ms of this, which is the cheapest bot filter that costs a real person
         nothing. Absent or garbled, it never blocks a submission. */
      const startedAt = form.querySelector("[data-audit-started-at]");
      if (startedAt) startedAt.value = String(Date.now());

      /* The Worker answers a native (no-JS) post with a 303 back to this page. If we
         arrive carrying that flag, the request already succeeded, so show the
         received state rather than an untouched form the visitor may fill twice. */
      try {
        if (new URLSearchParams(window.location.search).get("lead") === "received") {
          markAuditReceived(form);
          return;
        }
      } catch {}

      const endpoint = config.integrations.formEndpoint;
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        if (endpoint) runHumanAuditPost(form, endpoint);
        else runHumanAuditFallback(form);
      });
    });
  }

  upstreamBindTools();
  document.addEventListener("shopify:section:load", upstreamBindTools);
  document.addEventListener("shopify:section:reorder", upstreamBindTools);

  return {
    normalizeWebsiteUrl: normalizeWebsiteUrl,
    calculateGrade: calculateGrade,
    prepareSiteGrade: prepareSiteGrade,
    pageSpeedResult: pageSpeedResult,
    estimateProject: estimateProject,
    estimatorSummary: estimatorSummary,
    calculateSlowSiteCost: calculateSlowSiteCost,
    contrastResult: contrastResult,
    normalizeHexColor: normalizeHexColor,
    checklistForType: checklistForType,
    checklistProgress: checklistProgress,
    checklistTextExport: checklistTextExport,
    scheduleTimeline: scheduleTimeline,
    buildTimelineIcs: buildTimelineIcs,
    comparisonPercent: comparisonPercent,
    comparisonClip: comparisonClip,
    normalizeMockupSelection: normalizeMockupSelection,
    randomMockupSelection: randomMockupSelection,
    buildHumanAuditMailto: buildHumanAuditMailto,
    proxyBase: function () { return upstreamProxyBase; }
  };
}());
